import {useEffect, useMemo, useState} from 'react';
import {ResponseVehicle} from '../types/api';
import { useFeatureAppContent } from '../use-feature-app-content';

const MAX_IMAGES = 3;

const useImagesRender = (vehicles: ResponseVehicle[]) => {
  const { context } = useFeatureAppContent();
  const paddedImageUrls = useMemo(() => {
    const imageUrls = vehicles.map(
      (vehicle) => {
        const url = vehicle?.images?.filter((image) => image?.context === context)?.[0]?.url;
        if (url) {
          return url;
        }
        const fallback = vehicle?.images?.filter((image) => image?.label === 'SlideSideLeft')?.[0]?.url;
        return fallback;
      }
    );

    return [
      ...imageUrls,
      ...new Array(MAX_IMAGES - imageUrls.length).fill(undefined)
    ].slice(0, MAX_IMAGES);
  }, [vehicles]);

  const [loadedImages, setLoadedImages] = useState<
    (HTMLImageElement | undefined)[]
  >(new Array(MAX_IMAGES).fill(undefined));

  useEffect(() => {
    let isMounted = true;

    // Load images asynchronously
    Promise.all(
      paddedImageUrls.map(
        (url) =>
          new Promise((resolve) => {
            if (!url) return resolve(undefined);

            const img = new Image();
            img.src = url;
            img.onload = () => resolve(img);
            img.onerror = () => resolve(undefined);
          })
      )
    ).then((images) => {
      if (isMounted) {
        setLoadedImages(images as HTMLImageElement[]);
      }
    });

    return () => {
      isMounted = false; // Cleanup on unmount
    };
  }, [paddedImageUrls]);

  return loadedImages;
};

export default useImagesRender;
